
 // =========================================================
 // DEBUTS CROP
 // InventoryPage.jsx
 // INVENTARIO CENTRAL · PERFIL ADMINISTRATIVO
 // =========================================================

import {
  Activity,
  AlertTriangle,
 
  ArrowLeft,
  ArrowRight,
  Barcode,
  Camera,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ImagePlus,
  LoaderCircle,
  Package,
  PackageCheck,
  PackageSearch,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Warehouse,
  X,
  XCircle,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";

import "../../styles/InventoryPage.css";

// =========================================================
// CONFIGURACIÓN API
// =========================================================
//
// Desarrollo:
// VITE_API_URL=http://localhost:8000/api
//
// Producción:
// VITE_API_URL=https://api.debutscrop.com/api
//
// Las rutas se pueden ajustar aquí cuando compartas
// inventory/urls.py y inventory/views.py.
// =========================================================


// ==========================================================
// DEBUTS CROP — BIOLINK
// React + Django REST Framework
// Desarrollo y producción
// ==========================================================

// Desarrollo:
// VITE_API_URL=http://localhost:8000/api
// VITE_BACKEND_URL=http://localhost:8000
//
// Producción:
// VITE_API_URL=https://api.debutscrop.com/api
// VITE_BACKEND_URL=https://api.debutscrop.com


const API_BASE_URL = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.PROD
      ? "https://api.debutscrop.com/api"
      : "http://localhost:8000/api"
  )
).replace(/\/+$/, "");

const INVENTORY_ENDPOINT = "/inventory/";
const MOVEMENTS_ENDPOINT = "/inventory/movements/";
const SCANS_ENDPOINT = "/inventory/scans/";

// =========================================================
// HELPERS
// =========================================================

function readAuth() {
  try {
    const raw = localStorage.getItem("auth");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readToken() {
  const auth = readAuth();

  return (
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    null
  );
}

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.movements)) return data.movements;
  if (Array.isArray(data?.scans)) return data.scans;
  return [];
}



function number(value) {
  return new Intl.NumberFormat("es-CO").format(
    Number(value || 0)
  );
}

function dateTime(value) {
  if (!value) return "NO DISPONIBLE";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "NO DISPONIBLE";
  }

  return parsed.toLocaleString("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getErrorMessage(data, fallback) {
  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data && typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;

    if (Array.isArray(firstValue) && firstValue.length) {
      return `${firstKey}: ${firstValue[0]}`;
    }

    if (typeof firstValue === "string") {
      return `${firstKey}: ${firstValue}`;
    }
  }

  return fallback;
}

function imageUrl(value) {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    try {
      const base = new URL(API_BASE_URL);
      return `${base.origin}${value}`;
    } catch {
      return value;
    }
  }

  return value;
}

// =========================================================
// COMPONENTE
// =========================================================

export default function InventoryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("inventory");
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [scans, setScans] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmingScan, setConfirmingScan] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");

  const [selectedImage, setSelectedImage] = useState(null);
  const [scanOperation, setScanOperation] = useState("identify");
  const [selectedScan, setSelectedScan] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");

  const [scanForm, setScanForm] = useState({
    store: "",
    source_image: null,
  });

  // =======================================================
  // REQUEST CENTRAL
  // =======================================================

  const apiRequest = useCallback(async (
    endpoint,
    options = {}
  ) => {
    const token = readToken();

    if (!token) {
      navigate("/login", { replace: true });
      throw new Error("Debes iniciar sesión para acceder al inventario.");
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    if (
      options.body &&
      !(options.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        ...options,
        headers,
      }
    );

    if (response.status === 401) {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");

      window.dispatchEvent(new Event("authUpdated"));

      navigate("/login", { replace: true });

      throw new Error("Tu sesión expiró. Inicia sesión nuevamente.");
    }

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    if (response.status !== 204) {
      if (contentType.includes("application/json")) {
        data = await response.json().catch(() => null);
      } else {
        data = await response.text().catch(() => "");
      }
    }

    if (!response.ok) {
      throw new Error(
        getErrorMessage(
          data,
          `Error ${response.status} al consultar ${endpoint}.`
        )
      );
    }

    return data;
  }, [navigate]);

  // =======================================================
  // LOAD INVENTORY
  // =======================================================

  const loadInventory = useCallback(async () => {
    const data = await apiRequest(INVENTORY_ENDPOINT);
    setInventory(normalizeList(data));
  }, [apiRequest]);

  // =======================================================
  // LOAD MOVEMENTS
  // =======================================================

  const loadMovements = useCallback(async () => {
    const data = await apiRequest(MOVEMENTS_ENDPOINT);
    setMovements(normalizeList(data));
  }, [apiRequest]);

  // =======================================================
  // LOAD SCANS
  // =======================================================

  const loadScans = useCallback(async () => {
    const data = await apiRequest(SCANS_ENDPOINT);
    setScans(normalizeList(data));
  }, [apiRequest]);

  // =======================================================
  // LOAD ALL
  // =======================================================

  const loadAll = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");
    setNotice("");

    try {
      const results = await Promise.allSettled([
        loadInventory(),
        loadMovements(),
        loadScans(),
      ]);

      const failed = results.filter(
        (result) => result.status === "rejected"
      );

      if (failed.length === results.length) {
        throw failed[0].reason;
      }

      if (failed.length) {
        setNotice(
          "Algunas secciones no pudieron cargarse. Verifica las rutas del backend."
        );
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible conectar con el módulo de inventario."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadInventory, loadMovements, loadScans]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // =======================================================
  // INVENTORY FILTER
  // =======================================================

  const filteredInventory = useMemo(() => {
    const query = search.trim().toLowerCase();

    return inventory.filter((item) => {
      const text = [
        item.product_name,
        item.product_sku,
        item.store_name,
        item.product,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (query && !text.includes(query)) {
        return false;
      }

      const available = Number(
        item.available_quantity ??
        (
          Number(item.quantity || 0) -
          Number(item.reserved_quantity || 0)
        )
      );

      const low = Boolean(item.low_stock);
      const empty = Boolean(item.out_of_stock) || available <= 0;

      if (stockFilter === "low" && !low) return false;
      if (stockFilter === "empty" && !empty) return false;
      if (stockFilter === "available" && empty) return false;

      return true;
    });
  }, [inventory, search, stockFilter]);

  // =======================================================
  // SUMMARY
  // =======================================================

  const summary = useMemo(() => {
    const quantity = inventory.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    );

    const reserved = inventory.reduce(
      (total, item) => total + Number(item.reserved_quantity || 0),
      0
    );

    const available = inventory.reduce(
      (total, item) => total + Number(
        item.available_quantity ??
        (
          Number(item.quantity || 0) -
          Number(item.reserved_quantity || 0)
        )
      ),
      0
    );

    const lowStock = inventory.filter(
      (item) => Boolean(item.low_stock)
    ).length;

    const outOfStock = inventory.filter(
      (item) => Boolean(item.out_of_stock) ||
        Number(item.available_quantity ?? (
          Number(item.quantity || 0) -
          Number(item.reserved_quantity || 0)
        )) <= 0
    ).length;

    return {
      products: inventory.length,
      quantity,
      reserved,
      available,
      lowStock,
      outOfStock,
    };
  }, [inventory]);

  // =======================================================
  // SELECT IMAGE
  // =======================================================

  const onChooseImage = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowed = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (!allowed.includes(file.type)) {
      setError("Usa una imagen JPG, PNG o WEBP.");
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("La imagen no puede superar 10 MB.");
      event.target.value = "";
      return;
    }

    setError("");
    setScanForm((previous) => ({
      ...previous,
      source_image: file,
    }));

    setSelectedImage(URL.createObjectURL(file));
  };

  // =======================================================
  // SUBMIT PHOTO SCAN
  // =======================================================

  const submitScan = async (event) => {
    event.preventDefault();

    if (!scanForm.store) {
      setError("Ingresa el ID de la tienda para realizar el escaneo.");
      return;
    }

    if (!scanForm.source_image) {
      setError("Selecciona una fotografía del producto.");
      return;
    }

    const formData = new FormData();
    formData.append("store", scanForm.store);
    formData.append("source_image", scanForm.source_image);
    formData.append("operation", scanOperation);

    setUploading(true);
    setError("");
    setNotice("");

    try {
      const created = await apiRequest(
        SCANS_ENDPOINT,
        {
          method: "POST",
          body: formData,
        }
      );

      setSelectedScan(created);
      setNotice(
        "Fotografía enviada. El resultado depende del procesamiento configurado en el backend."
      );

      await loadScans();
      setActiveTab("scans");
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible enviar la fotografía."
      );
    } finally {
      setUploading(false);
    }
  };

  // =======================================================
  // CONFIRM SCAN MATCH
  // =======================================================
  //
  // Ruta prevista: POST /inventory/scans/{id}/confirm/
  // Se ajustará cuando compartas inventory/urls.py y views.py.
  // =======================================================

  const confirmScan = async (scan) => {
    const productId = Number(selectedProductId);

    if (!scan?.id || !productId) {
      setError("Selecciona un producto válido para confirmar la coincidencia.");
      return;
    }

    setConfirmingScan(scan.id);
    setError("");
    setNotice("");

    try {
      await apiRequest(
        `${SCANS_ENDPOINT}${scan.id}/confirm/`,
        {
          method: "POST",
          body: JSON.stringify({
            product: productId,
          }),
        }
      );

      setNotice("Coincidencia confirmada correctamente.");
      setSelectedScan(null);
      setSelectedProductId("");

      await Promise.allSettled([
        loadScans(),
        loadInventory(),
      ]);
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No se pudo confirmar la coincidencia. Verifica la ruta de confirmación."
      );
    } finally {
      setConfirmingScan(null);
    }
  };

  // =======================================================
  // SCAN DETAILS
  // =======================================================

  const openScan = (scan) => {
    setSelectedScan(scan);
    setSelectedProductId(scan.product ? String(scan.product) : "");
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <ProfilePageLayout
      title="Inventario"
      subtitle="Existencias, movimientos y reconocimiento fotográfico de productos."
      icon={Warehouse}
    >
      <main className="dc-inventory-page">
        <button
          type="button"
          className="dc-inventory-back"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={17} />
          VOLVER A MI PERFIL
        </button>

        <section className="dc-inventory-hero">
          <div className="dc-inventory-hero-copy">
            <span className="dc-inventory-eyebrow">
              DEBUTS CROP · CONTROL CENTRAL
            </span>

            <h1>
              INVENTARIO
              <span> INTELIGENTE</span>
            </h1>

            <p>
              Controla existencias, detecta faltantes y conecta fotografías
              con el catálogo de productos.
            </p>

            <div className="dc-inventory-hero-actions">
              <button
                type="button"
                className="dc-inventory-primary"
                onClick={() => {
                  setActiveTab("scan");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <Camera size={17} />
                ESCANEAR PRODUCTO
              </button>

              <button
                type="button"
                className="dc-inventory-secondary"
                onClick={() => loadAll(true)}
                disabled={refreshing}
              >
                {refreshing
                  ? <LoaderCircle size={17} className="dc-spin" />
                  : <RefreshCw size={17} />}
                ACTUALIZAR
              </button>
            </div>
          </div>

          <div className="dc-inventory-hero-art">
            <div className="dc-inventory-orbit dc-orbit-one" />
            <div className="dc-inventory-orbit dc-orbit-two" />
            <div className="dc-inventory-hero-icon">
              <PackageSearch size={70} strokeWidth={1.2} />
            </div>
            <span className="dc-inventory-floating dc-float-a">
              <Barcode size={17} /> SKU
            </span>
            <span className="dc-inventory-floating dc-float-b">
              <Sparkles size={17} /> VISUAL AI
            </span>
          </div>
        </section>

        {error && (
          <div className="dc-inventory-alert error" role="alert">
            <XCircle size={19} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Cerrar error"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-inventory-alert success" role="status">
            <CheckCircle2 size={19} />
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice("")}
              aria-label="Cerrar aviso"
            >
              <X size={17} />
            </button>
          </div>
        )}

        <section className="dc-inventory-stats">
          <article className="dc-inventory-stat">
            <div className="dc-stat-icon purple">
              <Package size={21} />
            </div>
            <span>PRODUCTOS INVENTARIADOS</span>
            <strong>{number(summary.products)}</strong>
            <small>Registros de inventario</small>
          </article>

          <article className="dc-inventory-stat">
            <div className="dc-stat-icon green">
              <Warehouse size={21} />
            </div>
            <span>UNIDADES REGISTRADAS</span>
            <strong>{number(summary.quantity)}</strong>
            <small>Existencia total</small>
          </article>

          <article className="dc-inventory-stat">
            <div className="dc-stat-icon blue">
              <PackageCheck size={21} />
            </div>
            <span>DISPONIBLES</span>
            <strong>{number(summary.available)}</strong>
            <small>{number(summary.reserved)} unidades reservadas</small>
          </article>

          <article className="dc-inventory-stat warning">
            <div className="dc-stat-icon orange">
              <AlertTriangle size={21} />
            </div>
            <span>ALERTAS DE STOCK</span>
            <strong>{number(summary.lowStock + summary.outOfStock)}</strong>
            <small>
              {number(summary.lowStock)} bajo · {number(summary.outOfStock)} agotado
            </small>
          </article>
        </section>

        <nav className="dc-inventory-tabs" aria-label="Secciones de inventario">
          <button
            type="button"
            className={activeTab === "inventory" ? "active" : ""}
            onClick={() => setActiveTab("inventory")}
          >
            <Package size={17} />
            EXISTENCIAS
          </button>

          <button
            type="button"
            className={activeTab === "movements" ? "active" : ""}
            onClick={() => setActiveTab("movements")}
          >
            <Activity size={17} />
            MOVIMIENTOS
          </button>

          <button
            type="button"
            className={activeTab === "scan" ? "active" : ""}
            onClick={() => setActiveTab("scan")}
          >
            <Camera size={17} />
            ESCANEAR FOTO
          </button>

          <button
            type="button"
            className={activeTab === "scans" ? "active" : ""}
            onClick={() => setActiveTab("scans")}
          >
            <Sparkles size={17} />
            RECONOCIMIENTO
            {scans.length > 0 && (
              <span className="dc-tab-count">{scans.length}</span>
            )}
          </button>
        </nav>

        {loading ? (
          <section className="dc-inventory-panel dc-inventory-loading">
            <LoaderCircle size={30} className="dc-spin" />
            <strong>CARGANDO MÓDULO DE INVENTARIO...</strong>
            <span>Conectando con el backend de Debuts Crop</span>
          </section>
        ) : (
          <>
            {activeTab === "inventory" && (
              <section className="dc-inventory-panel">
                <div className="dc-inventory-panel-heading">
                  <div>
                    <span className="dc-inventory-eyebrow">CONTROL DE STOCK</span>
                    <h2>Existencias por producto</h2>
                    <p>La disponibilidad se calcula con el inventario del backend.</p>
                  </div>
                  <span className="dc-inventory-total-badge">
                    {filteredInventory.length} REGISTROS
                  </span>
                </div>

                <div className="dc-inventory-toolbar">
                  <label className="dc-inventory-search">
                    <Search size={18} />
                    <input
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar producto, SKU o tienda..."
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Limpiar búsqueda"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </label>

                  <label className="dc-inventory-filter">
                    <span>FILTRAR</span>
                    <select
                      value={stockFilter}
                      onChange={(event) => setStockFilter(event.target.value)}
                    >
                      <option value="all">Todos los estados</option>
                      <option value="available">Con disponibilidad</option>
                      <option value="low">Stock bajo</option>
                      <option value="empty">Agotados</option>
                    </select>
                    <ChevronDown size={15} />
                  </label>
                </div>

                {filteredInventory.length === 0 ? (
                  <div className="dc-inventory-empty">
                    <PackageSearch size={42} />
                    <strong>NO HAY REGISTROS PARA MOSTRAR</strong>
                    <span>
                      Comprueba los filtros o verifica que existan registros de inventario.
                    </span>
                  </div>
                ) : (
                  <div className="dc-inventory-table-wrap">
                    <table className="dc-inventory-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIENDA</th>
                          <th>EXISTENCIA</th>
                          <th>RESERVADO</th>
                          <th>DISPONIBLE</th>
                          <th>ESTADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInventory.map((item) => {
                          const available = Number(
                            item.available_quantity ??
                            (
                              Number(item.quantity || 0) -
                              Number(item.reserved_quantity || 0)
                            )
                          );

                          const low = Boolean(item.low_stock);
                          const empty = Boolean(item.out_of_stock) || available <= 0;

                          return (
                            <tr key={item.id}>
                              <td>
                                <div className="dc-inventory-product-cell">
                                  <div className="dc-inventory-product-image">
                                    {item.product_image ? (
                                      <img
                                        src={imageUrl(item.product_image)}
                                        alt={item.product_name || "Producto"}
                                        loading="lazy"
                                      />
                                    ) : (
                                      <Package size={22} />
                                    )}
                                  </div>
                                  <div>
                                    <strong>{item.product_name || `Producto #${item.product}`}</strong>
                                    <span>SKU: {item.product_sku || "SIN SKU"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>{item.store_name || `Tienda #${item.store_id || "—"}`}</td>
                              <td><strong>{number(item.quantity)}</strong></td>
                              <td>{number(item.reserved_quantity)}</td>
                              <td>
                                <strong className={empty ? "dc-quantity-empty" : ""}>
                                  {number(available)}
                                </strong>
                              </td>
                              <td>
                                {empty ? (
                                  <span className="dc-stock-status empty">
                                    <XCircle size={14} /> AGOTADO
                                  </span>
                                ) : low ? (
                                  <span className="dc-stock-status low">
                                    <AlertTriangle size={14} /> STOCK BAJO
                                  </span>
                                ) : (
                                  <span className="dc-stock-status good">
                                    <CheckCircle2 size={14} /> DISPONIBLE
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {activeTab === "movements" && (
              <section className="dc-inventory-panel">
                <div className="dc-inventory-panel-heading">
                  <div>
                    <span className="dc-inventory-eyebrow">AUDITORÍA</span>
                    <h2>Historial de movimientos</h2>
                    <p>Entradas, salidas y operaciones registradas por el backend.</p>
                  </div>
                  <span className="dc-inventory-total-badge">
                    {movements.length} MOVIMIENTOS
                  </span>
                </div>

                {movements.length === 0 ? (
                  <div className="dc-inventory-empty">
                    <Activity size={42} />
                    <strong>SIN MOVIMIENTOS REGISTRADOS</strong>
                    <span>
                      Los movimientos aparecerán aquí cuando el backend los registre.
                    </span>
                  </div>
                ) : (
                  <div className="dc-inventory-table-wrap">
                    <table className="dc-inventory-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIPO</th>
                          <th>CANTIDAD</th>
                          <th>RESPONSABLE</th>
                          <th>VENTA</th>
                          <th>FECHA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((movement) => (
                          <tr key={movement.id}>
                            <td>
                              <div className="dc-movement-product">
                                <strong>
                                  {movement.product_name || `Producto #${movement.product}`}
                                </strong>
                                <span>{movement.product_sku || "SIN SKU"}</span>
                                {movement.description && (
                                  <small>{movement.description}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="dc-movement-type">
                                {movement.movement_type_display ||
                                  movement.movement_type ||
                                  "MOVIMIENTO"}
                              </span>
                            </td>
                            <td><strong>{number(movement.quantity)}</strong></td>
                            <td>{movement.username || "SISTEMA"}</td>
                            <td>{movement.sale_number || "—"}</td>
                            <td>{dateTime(movement.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {activeTab === "scan" && (
              <section className="dc-inventory-panel">
                <div className="dc-inventory-panel-heading">
                  <div>
                    <span className="dc-inventory-eyebrow">VISIÓN ARTIFICIAL</span>
                    <h2>Reconocer producto por fotografía</h2>
                    <p>
                      Envía la imagen al backend para identificar, preparar o actualizar un producto.
                    </p>
                  </div>
                  <div className="dc-scan-badge">
                    <Sparkles size={16} /> SCAN LAB
                  </div>
                </div>

                <form className="dc-scan-form" onSubmit={submitScan}>
                  <div className="dc-scan-fields">
                    <label>
                      <span>ID DE LA TIENDA</span>
                      <input
                        type="number"
                        min="1"
                        value={scanForm.store}
                        onChange={(event) =>
                          setScanForm((previous) => ({
                            ...previous,
                            store: event.target.value,
                          }))
                        }
                        placeholder="Ej. 1"
                        required
                      />
                      <small>Debe ser una tienda autorizada para tu usuario.</small>
                    </label>

                    <label>
                      <span>OPERACIÓN</span>
                      <select
                        value={scanOperation}
                        onChange={(event) => setScanOperation(event.target.value)}
                      >
                        <option value="identify">Identificar producto</option>
                        <option value="create">Preparar creación de producto</option>
                        <option value="update">Preparar actualización de producto</option>
                      </select>
                      <small>El escaneo no cambia el inventario por sí solo.</small>
                    </label>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="dc-scan-hidden-input"
                    onChange={onChooseImage}
                  />

                  <button
                    type="button"
                    className={`dc-scan-dropzone ${selectedImage ? "has-image" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedImage ? (
                      <>
                        <img src={selectedImage} alt="Vista previa del producto" />
                        <span className="dc-scan-change-image">
                          <ImagePlus size={17} /> CAMBIAR FOTOGRAFÍA
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="dc-scan-upload-icon">
                          <Upload size={28} />
                        </span>
                        <strong>SUBE LA FOTO DEL PRODUCTO</strong>
                        <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                        <small>Haz clic para seleccionar una imagen</small>
                      </>
                    )}
                  </button>

                  {scanForm.source_image && (
                    <div className="dc-scan-file">
                      <ImagePlus size={18} />
                      <span>{scanForm.source_image.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedImage(null);
                          setScanForm((previous) => ({
                            ...previous,
                            source_image: null,
                          }));
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        aria-label="Eliminar fotografía"
                      >
                        <X size={17} />
                      </button>
                    </div>
                  )}

                  <div className="dc-scan-security-note">
                    <ShieldCheck size={18} />
                    <span>
                      La creación o edición definitiva debe validarse en el backend.
                      Revisa las sugerencias antes de aplicar cambios.
                    </span>
                  </div>

                  <button
                    type="submit"
                    className="dc-inventory-primary dc-scan-submit"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <LoaderCircle size={18} className="dc-spin" />
                    ) : (
                      <Camera size={18} />
                    )}
                    {uploading ? "ENVIANDO IMAGEN..." : "ENVIAR AL SISTEMA VISUAL"}
                    {!uploading && <ArrowRight size={17} />}
                  </button>
                </form>
              </section>
            )}

            {activeTab === "scans" && (
              <section className="dc-inventory-panel">
                <div className="dc-inventory-panel-heading">
                  <div>
                    <span className="dc-inventory-eyebrow">RESULTADOS DEL SISTEMA</span>
                    <h2>Escaneos y coincidencias</h2>
                    <p>Consulta el estado de cada fotografía procesada.</p>
                  </div>
                  <button
                    type="button"
                    className="dc-inventory-secondary"
                    onClick={loadScans}
                  >
                    <RefreshCw size={16} /> RECARGAR
                  </button>
                </div>

                {scans.length === 0 ? (
                  <div className="dc-inventory-empty">
                    <Camera size={42} />
                    <strong>TODAVÍA NO HAY ESCANEOS</strong>
                    <span>Sube una fotografía para iniciar el reconocimiento.</span>
                    <button
                      type="button"
                      className="dc-inventory-primary"
                      onClick={() => setActiveTab("scan")}
                    >
                      <Plus size={17} /> NUEVO ESCANEO
                    </button>
                  </div>
                ) : (
                  <div className="dc-scan-results">
                    {scans.map((scan) => {
                      const confidence = Number(
                        scan.confidence_percentage ??
                        Number(scan.confidence || 0) * 100
                      );

                      return (
                        <article className="dc-scan-result" key={scan.id}>
                          <div className="dc-scan-result-image">
                            {scan.processed_image || scan.source_image ? (
                              <img
                                src={imageUrl(
                                  scan.processed_image || scan.source_image
                                )}
                                alt={scan.product_name || "Escaneo"}
                              />
                            ) : (
                              <Camera size={26} />
                            )}
                          </div>

                          <div className="dc-scan-result-main">
                            <div className="dc-scan-result-title">
                              <strong>
                                {scan.product_name ||
                                  scan.suggested_data?.name ||
                                  `ESCANEO #${scan.id}`}
                              </strong>
                              <span className={`dc-scan-status ${String(scan.status || "pending").toLowerCase()}`}>
                                {scan.status_display || scan.status || "PENDIENTE"}
                              </span>
                            </div>

                            <p>
                              {scan.operation_display || scan.operation || "Identificación"}
                              {" · "}
                              {scan.store_name || `Tienda #${scan.store || "—"}`}
                            </p>

                            <div className="dc-scan-result-meta">
                              <span>
                                <Clock3 size={14} /> {dateTime(scan.created_at)}
                              </span>
                              {scan.product_sku && (
                                <span>SKU: {scan.product_sku}</span>
                              )}
                              {scan.recognition_provider && (
                                <span>{scan.recognition_provider}</span>
                              )}
                            </div>

                            {Number.isFinite(confidence) && confidence > 0 && (
                              <div className="dc-scan-confidence">
                                <div>
                                  <span>CONFIANZA</span>
                                  <strong>{Math.round(confidence)}%</strong>
                                </div>
                                <div className="dc-confidence-track">
                                  <span
                                    style={{
                                      width: `${Math.min(100, Math.max(0, confidence))}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {scan.extracted_text && (
                              <details className="dc-scan-text-details">
                                <summary>Ver texto extraído</summary>
                                <p>{scan.extracted_text}</p>
                              </details>
                            )}
                          </div>

                          <div className="dc-scan-result-actions">
                            <button
                              type="button"
                              className="dc-inventory-secondary"
                              onClick={() => openScan(scan)}
                            >
                              VER DETALLE <ArrowRight size={15} />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </>
        )}

        <footer className="dc-inventory-footer">
          <div>
            <ShieldCheck size={17} />
            <span>INVENTARIO CENTRALIZADO · DEBUTS CROP</span>
          </div>
          <span>LOS CAMBIOS DE STOCK SE VALIDAN EN EL SERVIDOR</span>
        </footer>
      </main>

      {selectedScan && (
        <div
          className="dc-inventory-modal-backdrop"
          onMouseDown={() => setSelectedScan(null)}
        >
          <section
            className="dc-inventory-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-scan-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span className="dc-inventory-eyebrow">RECONOCIMIENTO VISUAL</span>
                <h2 id="dc-scan-modal-title">
                  Escaneo #{selectedScan.id}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedScan(null)}
                aria-label="Cerrar detalle"
              >
                <X size={21} />
              </button>
            </header>

            {(selectedScan.processed_image || selectedScan.source_image) && (
              <img
                className="dc-inventory-modal-image"
                src={imageUrl(
                  selectedScan.processed_image || selectedScan.source_image
                )}
                alt="Imagen del escaneo"
              />
            )}

            <div className="dc-inventory-modal-info">
              <div>
                <span>ESTADO</span>
                <strong>
                  {selectedScan.status_display || selectedScan.status || "PENDIENTE"}
                </strong>
              </div>
              <div>
                <span>OPERACIÓN</span>
                <strong>
                  {selectedScan.operation_display || selectedScan.operation || "IDENTIFICAR"}
                </strong>
              </div>
              <div>
                <span>PRODUCTO RELACIONADO</span>
                <strong>
                  {selectedScan.product_name ||
                    selectedScan.suggested_data?.name ||
                    "SIN PRODUCTO CONFIRMADO"}
                </strong>
              </div>
              <div>
                <span>TIENDA</span>
                <strong>{selectedScan.store_name || selectedScan.store || "—"}</strong>
              </div>
            </div>

            {selectedScan.suggested_data && (
              <details className="dc-scan-suggestions" open>
                <summary>Datos sugeridos por el reconocimiento</summary>
                <pre>
                  {JSON.stringify(selectedScan.suggested_data, null, 2)}
                </pre>
              </details>
            )}

            {selectedScan.metadata && (
              <details className="dc-scan-suggestions">
                <summary>Metadatos técnicos</summary>
                <pre>{JSON.stringify(selectedScan.metadata, null, 2)}</pre>
              </details>
            )}

            <div className="dc-inventory-confirm">
              <label>
                <span>ID DEL PRODUCTO EXISTENTE</span>
                <input
                  type="number"
                  min="1"
                  value={selectedProductId}
                  onChange={(event) => setSelectedProductId(event.target.value)}
                  placeholder="Ingresa el ID para confirmar coincidencia"
                />
              </label>

              <p>
                Esta acción confirma una coincidencia con un producto existente.
                No crea productos ni modifica cantidades de inventario.
              </p>

              <button
                type="button"
                className="dc-inventory-primary"
                onClick={() => confirmScan(selectedScan)}
                disabled={confirmingScan === selectedScan.id}
              >
                {confirmingScan === selectedScan.id ? (
                  <LoaderCircle size={17} className="dc-spin" />
                ) : (
                  <CheckCircle2 size={17} />
                )}
                CONFIRMAR COINCIDENCIA
              </button>
            </div>
          </section>
        </div>
      )}
    </ProfilePageLayout>
  );
}
